/* Killer Golf Fit Quiz — quiz logic + Shopify cart integration. (v4.3 / 2026-05-20)
 * Original quiz logic from HTML draft 2026-05-06; cart integration is new.
 *
 * Architecture notes:
 *   - SKU mapping lives entirely in kg-fit-quiz-skus.js. If a product handle is wrong,
 *     edit that file only.
 *   - Variant resolution is at runtime via /products/{handle}.js — no stale variant IDs.
 *   - Cart-add posts to /cart/add.js and redirects to /cart on success.
 *   - Fit metadata travels as line item properties on the base putter line only.
 */
(function () {
  /* Version marker — lets us verify the LIVE file matches the source when debugging.
     Bump the suffix on every meaningful release. */
  console.log('[kg-fit-quiz] v4.5 loaded (' + new Date().toISOString().slice(0,16) + ')');
  if (!window.KG_FIT_QUIZ_SKUS) {
    console.error('[kg-fit-quiz] SKU map missing — kg-fit-quiz-skus.js must load first.');
    return;
  }
  var SKUS = window.KG_FIT_QUIZ_SKUS;

  /* Debug logger — silenced in production. Enable with `window.KG_DEBUG = true`
     in DevTools, or `?kg_debug=1` in the URL. customerId / payload / cart state
     contain PII and internal market info; do not log them unconditionally. */
  var DEBUG = (function () {
    try {
      if (window.KG_DEBUG) return true;
      if (typeof location !== 'undefined' && /[?&]kg_debug=1\b/.test(location.search)) {
        window.KG_DEBUG = true;
        return true;
      }
    } catch (e) {}
    return false;
  })();
  function debug() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[kg-fit-quiz]');
      console.log.apply(console, args);
    } catch (e) {}
  }
  function debugGroup(label) { if (DEBUG) { try { console.group('[kg-fit-quiz] ' + label); } catch (e) {} } }
  function debugGroupEnd()    { if (DEBUG) { try { console.groupEnd(); } catch (e) {} } }

  function defaultState() {
    return {
      buildPath: null,
      missType: null,
      head: null,
      system: null,
      artifact: null,
      color: null,
      shaftLength: null,
      shaftColor: null,
      gripThickness: null,
      gripColor: null,
      handOrientation: 'Right Hand', // Q4: LH disabled, RH default
      strokeType: null,
      greenSpeed: null,
      feelPreference: null,
      missDetail: null,
      recommendedAnchor: null,
      recommendedGrip: null,
      recommendedTuning: null,
      recommendationScore: null
    };
  }

  var state = defaultState();

  var guidedRecommendations = {
    Short: { head: 'Wing', system: 'EQuilibrium System', reason: 'For putts left short, we recommend a stability-first build with EQuilibrium tuning to support smoother tempo and distance control.' },
    Push: { head: 'Blade', system: 'CenterStrike System', reason: 'For pushes, we recommend CenterStrike so the player can focus on face delivery, impact feedback, and start-line control.' },
    Pull: { head: 'Blade', system: 'CenterStrike System', reason: 'For pulls, we recommend CenterStrike to help refine face closure, strike feedback, and release timing.' },
    Inconsistent: { head: 'Wing', system: 'CenterStrike System', reason: 'For inconsistent strike, we recommend combining a stable head shape with clearer center-contact feedback.' }
  };

  /* Catalog override: Wing/Blade head prices. Liquid may inject current product.price so
     getBasePrice/getTotalPrice match the price badge on the storefront. Falls back to
     hardcoded headPrices in the SKU map. Heads are display-only — not in cart. */
  (function applyHeadPriceCatalog() {
    var catalog = window.kgFitQuizCatalog || {};
    var prices = catalog.headPrices;
    if (!prices) return;
    SKUS.headPrices = SKUS.headPrices || { Wing: 525, Blade: 495 };
    if (typeof prices.Wing  === 'number' && prices.Wing  > 0) SKUS.headPrices.Wing  = prices.Wing;
    if (typeof prices.Blade === 'number' && prices.Blade > 0) SKUS.headPrices.Blade = prices.Blade;
  })();

  /* No-op stub — kept so any legacy Liquid that injects window.kgFitQuizCatalog.artifactVariants
     doesn't blow up. v4 artifact themes are static in the SKU map (artifactThemes). */
  (function applyArtifactCatalog() {
    return;
  })();

  /* Artifact theme list — used to render the system-step artifact picker.
   * v2: artifacts are variants of a single Artifact Putter product.
   * v2.1: imageUrl carries the per-theme product photo. Placeholder strings (PUT-YOUR-…)
   * fall back to a swatch-placeholder div until real URLs are pasted in. */
  /* artifactItems is rendered for the system-step artifact picker. cart-add lookup
     happens later via SKUS.kits['Artifact System'].variants[state.artifact]. */
  var artifactItems = (SKUS.artifactThemes || []).map(function (t) {
    return { value: t.label, label: t.label, imageUrl: t.imageUrl };
  });

  function isLiveImageUrl(u) {
    return typeof u === 'string' && /^(https?:\/\/|\/)/.test(u);
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  var quizShell, stepsContainer, steps, backButton, nextButton;
  var progressLabel, progressPercent, progressFill, summaryList, finalPrice, addToCartButton;
  var cartStatus, customizeRecommendedButton, changeMissButton, addRecommendedToCartButton;
  var closeRecommendedButton, recommendedCartStatus, recommendedPrice, recommendedColorPanel;
  var confirmRecommendedCartButton, recommendedColorError;
  var currentStep = 0;

  /* Light-mode toggle was scrapped early — force dark and clear any stale localStorage prefs. */
  function initializeTheme() {
    document.body.classList.remove('light-mode');
    try { localStorage.removeItem('killerGolfQuizTheme'); } catch (e) {}
  }

  function money(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  }

  /* All builds — Wing/Blade head drives the base price. Artifact System keeps Wing/Blade
     as the head and adds the chosen artifact theme as a second-line upgrade; the Discount
     Function (kg-artifact-discount) collapses the total to a flat target price ($400) when
     an artifact-putter line is present. The recommendation panel still previews that target
     for Artifact builds via getTotalPrice(). */
  var ARTIFACT_TARGET_PRICE = 400;

  function getBasePrice() {
    var prices = SKUS.headPrices || {};
    return (state.head && prices[state.head]) || 0;
  }

  function getTotalPrice() {
    if (state.system === 'Artifact System') return ARTIFACT_TARGET_PRICE;
    return getBasePrice();
  }

  function getIncludedComponents() {
    /* Artifact: picked theme.
       EQ / CS: list every variant in the kit (matches what buildLineItems() ships to cart). */
    if (state.system === 'Artifact System') {
      return state.artifact ? ('Artifact: ' + state.artifact) : 'Artifact';
    }
    var kit = SKUS.kits[state.system];
    if (!kit || !kit.variants) return '-';
    var names = [];
    for (var k in kit.variants) {
      if (Object.prototype.hasOwnProperty.call(kit.variants, k)) names.push(k);
    }
    if (names.length === 0) return kit.label || '-';
    return kit.label + ' (' + names.join(' + ') + ')';
  }

  function setSelectedCard(field, value) {
    var cards = document.querySelectorAll('[data-field="' + field + '"] .option-card');
    for (var i = 0; i < cards.length; i++) cards[i].classList.toggle('selected', cards[i].dataset.value === value);
    var rcards = document.querySelectorAll('[data-rec-field="' + field + '"] .color-choice-card');
    for (var k = 0; k < rcards.length; k++) rcards[k].classList.toggle('selected', rcards[k].dataset.value === value);
  }

  function getDetailedRecommendation() {
    var base = guidedRecommendations[state.missType];
    if (!base) return null;
    var scores = {
      wing: base.head === 'Wing' ? 2 : 0,
      blade: base.head === 'Blade' ? 2 : 0,
      eq: base.system === 'EQuilibrium System' ? 2 : 0,
      cs: base.system === 'CenterStrike System' ? 2 : 0
    };
    var notes = [base.reason];
    if (state.strokeType === 'Straight') { scores.wing += 2; scores.eq += 1; notes.push('Your straight-back-straight-through stroke favors higher face balance and stability.'); }
    if (state.strokeType === 'Slight Arc') { scores.blade += 1; scores.cs += 1; notes.push('Your slight arc benefits from added face feedback without giving up control.'); }
    if (state.strokeType === 'Strong Arc') { scores.blade += 2; scores.cs += 1; notes.push('Your stronger arc benefits from a more responsive head profile and clear release feedback.'); }
    if (state.greenSpeed === 'Slow') { scores.eq += 2; notes.push('Slow greens usually reward added mass and smoother tempo support.'); }
    if (state.greenSpeed === 'Fast') { scores.cs += 1; notes.push('Fast greens usually reward precise face feedback and lighter-feeling control.'); }
    if (state.feelPreference === 'Soft') { scores.eq += 1; notes.push('A softer feel preference points toward smoother weight tuning.'); }
    if (state.feelPreference === 'Firm') { scores.cs += 1; notes.push('A firmer feel preference points toward sharper strike feedback.'); }
    if (state.missDetail === 'Heel' || state.missDetail === 'Toe') { scores.wing += 1; scores.cs += 1; notes.push('Heel/toe strike misses benefit from stability plus clearer feedback on off-center contact.'); }
    if (state.missDetail === 'Center') { scores.eq += 1; notes.push('Center contact with inconsistent results points toward tempo and distance-control tuning.'); }
    var head = scores.wing >= scores.blade ? 'Wing' : 'Blade';
    var system = scores.cs > scores.eq ? 'CenterStrike System' : 'EQuilibrium System';
    if (state.missType === 'Inconsistent' && (state.missDetail === 'Heel' || state.missDetail === 'Toe')) system = 'CenterStrike System';
    var recommendedAnchor = 'Anchor 50 baseline';
    if (system === 'CenterStrike System') {
      if (state.feelPreference === 'Firm' || state.greenSpeed === 'Fast') recommendedAnchor = 'Anchor 25 or Anchor 50 for quicker feedback';
      if (state.feelPreference === 'Balanced' || state.strokeType === 'Slight Arc') recommendedAnchor = 'Anchor 50 baseline';
      if (state.feelPreference === 'Soft' || state.greenSpeed === 'Slow') recommendedAnchor = 'Anchor 75 for added stability';
      if (state.missType === 'Inconsistent' || state.missDetail === 'Toe' || state.missDetail === 'Heel') recommendedAnchor = 'Anchor 75 baseline, test Anchor 150 for maximum stability';
    }
    var recommendedTuning = system === 'EQuilibrium System' ? 'B40 + EQ65 included' : recommendedAnchor;
    if (system === 'EQuilibrium System' && state.greenSpeed === 'Fast') recommendedTuning = 'B40 baseline, test EQ65 only if distance feels short';
    if (system === 'EQuilibrium System' && state.greenSpeed === 'Slow') recommendedTuning = 'B40 + EQ65 for smoother tempo and added roll energy';
    var recommendedGrip = 'Medium';
    if (state.feelPreference === 'Firm' || state.strokeType === 'Strong Arc') recommendedGrip = 'Thin';
    if (state.missType === 'Inconsistent' || state.feelPreference === 'Soft') recommendedGrip = 'Thick';
    var dominance = Math.abs(scores.wing - scores.blade) + Math.abs(scores.eq - scores.cs);
    var confidence = 'Medium';
    if (dominance >= 3) confidence = 'High';
    if (dominance <= 1) confidence = 'Low';
    var reason = notes.join(' ');
    if (confidence === 'Low') reason += ' Your answers show mixed signals between fitting profiles, so use this as a starting point and consider customizing head style, system weighting, or grip feel.';
    if (confidence === 'High') reason += ' Your answers strongly align with this configuration.';
    return { head: head, system: system, recommendedAnchor: recommendedAnchor, recommendedGrip: recommendedGrip, recommendedTuning: recommendedTuning, score: scores, confidence: confidence, reason: reason };
  }

  function getSwatchLabel(type, value) { return value ? value : type; }

  /* Recommendation-panel image swatches. Single source of truth = the step-card
     <img> already rendered in the page (Step 1 / Step 2 / Step 7 liquid). Pull
     src by [data-field][data-value] selector so liquid edits auto-propagate.
     Returns null if the option doesn't exist or hasn't been rendered yet. */
  function findStepCardImage(field, value) {
    if (!field || !value) return null;
    var img = document.querySelector(
      '[data-field="' + field + '"] [data-value="' + value + '"] .image-swatch img'
    );
    return img && img.src ? img.src : null;
  }

  function setSwatch(id, label, field) {
    var swatch = document.getElementById(id);
    if (!swatch) return;
    var url = findStepCardImage(field, label);
    if (url) {
      swatch.innerHTML = '<img src="' + escapeAttr(url) + '" alt="' + escapeAttr(label) + '" loading="lazy">';
    } else {
      swatch.innerHTML = '<div class="swatch-placeholder">' + escapeAttr(label) + '</div>';
    }
  }

  function applyRefinedDefaults() {
    var rec = getDetailedRecommendation();
    if (!rec) return;
    state.head = rec.head;
    state.system = rec.system;
    /* Recommendation only ever returns EQ or CenterStrike for system. If the user previously
       picked Artifact System + a theme, those selections are no longer valid for this build —
       clear the artifact state so it doesn't bleed into cart properties as stale data. */
    if (state.system !== 'Artifact System' && state.artifact) {
      state.artifact = null;
      var arts = document.querySelectorAll('.artifact-card');
      for (var i = 0; i < arts.length; i++) arts[i].classList.remove('selected');
    }
    state.recommendedAnchor = rec.recommendedAnchor;
    state.recommendedGrip = rec.recommendedGrip;
    state.recommendedTuning = rec.recommendedTuning;
    state.recommendationScore = rec.score;
    if (!state.gripThickness) state.gripThickness = rec.recommendedGrip;
    if (!state.handOrientation) state.handOrientation = 'Right Hand';
    setSelectedCard('head', state.head);
    setSelectedCard('system', state.system);
    setSelectedCard('gripThickness', state.gripThickness);
    setSelectedCard('handOrientation', state.handOrientation);
    updateArtifactGridVisibility();
    updateRecommendedBuildScreen();
    updateGuidedBanners();
    updateSystemDetails();
    updateArtifactSavingsBadge();
  }

  function applyGuidedRecommendation() {
    if (state.buildPath !== 'Guided') return;
    applyRefinedDefaults();
  }

  function updateRecommendedBuildScreen() {
    var rec = getDetailedRecommendation();
    document.getElementById('recommendedMiss').textContent = state.missType || '-';
    document.getElementById('recommendedHead').textContent = rec ? rec.head : '-';
    document.getElementById('recommendedSystem').textContent = rec ? rec.system : '-';
    document.getElementById('recommendedTuning').textContent = rec ? rec.recommendedTuning : '-';
    document.getElementById('recommendedGrip').textContent = rec ? rec.recommendedGrip : '-';
    setSwatch('recommendedHeadSwatch',    getSwatchLabel('Head',    rec ? rec.head : null),               'head');
    setSwatch('recommendedSystemSwatch',  getSwatchLabel('System',  rec ? rec.system : null),             'system');
    setSwatch('recommendedGripSwatch',    getSwatchLabel('Grip',    rec ? rec.recommendedGrip : null),    'gripThickness');
    /* Tuning swatch removed — value now lives in the fit-tuning-panel beside the
       confidence panel. The #recommendedTuning textContent update above still
       targets the new panel's value cell. */
    document.getElementById('recommendedConfidence').textContent = rec ? rec.confidence : '-';
    document.getElementById('recommendedReason').textContent = rec ? rec.reason : 'Answer the fitting questions to see your recommended build.';
    if (recommendedPrice) recommendedPrice.textContent = money(getTotalPrice());
  }

  function updateGuidedBanners() {
    var rec = getDetailedRecommendation();
    var active = state.buildPath === 'Guided' && Boolean(rec);
    var headBanner = document.getElementById('headGuidedBanner');
    var systemBanner = document.getElementById('systemGuidedBanner');
    if (headBanner) headBanner.classList.toggle('active', active);
    if (systemBanner) systemBanner.classList.toggle('active', active);
    var headText = document.getElementById('headGuidedText');
    var systemText = document.getElementById('systemGuidedText');
    if (headText) headText.textContent = active ? rec.reason : '';
    if (systemText) systemText.textContent = active ? rec.reason : '';
  }

  function getStepIndexByName(name) {
    for (var i = 0; i < steps.length; i++) if (steps[i].dataset.step === name) return i;
    return -1;
  }

  /* Steps that only the Guided path needs. Manual path skips them so the smooth
     horizontal scroll from `path` to `head` doesn't flash through their unfilled
     placeholder panels. Toggled via .hidden ⇒ display:none (CSS line ~384). */
  var GUIDED_ONLY_STEP_NAMES = ['miss', 'fit-stroke', 'fit-speed', 'fit-feel', 'fit-miss-detail', 'recommendation'];

  function applyBuildPathStepVisibility() {
    if (!steps || !steps.length) return;
    var hideGuided = state.buildPath === 'Manual';
    for (var i = 0; i < steps.length; i++) {
      if (GUIDED_ONLY_STEP_NAMES.indexOf(steps[i].dataset.step) !== -1) {
        steps[i].classList.toggle('hidden', hideGuided);
      }
    }
  }

  function countVisibleSteps() {
    var n = 0;
    for (var i = 0; i < steps.length; i++) {
      if (!steps[i].classList.contains('hidden')) n++;
    }
    return n;
  }

  function visibleStepNumber(domIndex) {
    /* 1-based ordinal of the given DOM index among non-hidden steps. */
    var n = 0;
    for (var i = 0; i <= domIndex && i < steps.length; i++) {
      if (!steps[i].classList.contains('hidden')) n++;
    }
    return n;
  }

  /* Force the steps carousel to the leftmost (step 0) position, robust against
     Safari's mandatory-snap restore behavior. Setting scrollLeft = 0 alone is
     overridden by Safari when the container has `scroll-snap-type: x mandatory`
     and a previous snap point is "remembered" across a display:none ↔ block
     toggle. The fix: temporarily disable snap, set scrollLeft to 0, then
     re-enable snap so it commits the NEW position (0) as the active snap point.
     Discovered via: containerScrollLeft observed at exactly 10×clientWidth
     after Add to Cart → reopen, with no code path scrolling to that position
     after our reset. */
  function forceScrollToStart() {
    if (!stepsContainer) return;
    var prevSnap = stepsContainer.style.scrollSnapType;
    var prevBehavior = stepsContainer.style.scrollBehavior;
    stepsContainer.style.scrollSnapType = 'none';
    stepsContainer.style.scrollBehavior = 'auto';
    stepsContainer.scrollLeft = 0;
    // Restore on next frame so the browser commits the new position first.
    requestAnimationFrame(function () {
      stepsContainer.style.scrollSnapType = prevSnap || '';
      stepsContainer.style.scrollBehavior = prevBehavior || '';
    });
  }

  function scrollToStep(index) {
    var direction = index >= currentStep ? 1 : -1;
    var target = Math.max(0, Math.min(index, steps.length - 1));
    // Skip any step flagged hidden — general guard for future conditional steps.
    while (target > 0 && target < steps.length - 1 && steps[target].classList.contains('hidden')) {
      target += direction;
    }
    currentStep = target;
    steps[currentStep].scrollIntoView({ behavior: 'smooth', inline: 'start' });
    updateProgress();
    updateButtons();
    if (steps[currentStep].dataset.step === 'final') renderSummary();
    if (steps[currentStep].dataset.step === 'system') updateArtifactSavingsBadge();
  }

  function updateProgress() {
    var total = countVisibleSteps();
    var stepNumber = visibleStepNumber(currentStep);
    var percent = total > 0 ? Math.round((stepNumber / total) * 100) : 0;
    progressLabel.textContent = 'Step ' + stepNumber + ' of ' + total;
    progressPercent.textContent = percent + '%';
    progressFill.style.width = percent + '%';
  }

  function updateButtons() {
    backButton.disabled = currentStep === 0;
    var stepName = steps[currentStep].dataset.step;
    if (stepName === 'recommendation' || stepName === 'final') {
      nextButton.style.display = 'none';
      return;
    }
    /* "Review Build" appears on the second-to-last VISIBLE step (so the next click
       lands on `final`). Counting visible steps keeps this label correct whether
       Guided refinement steps are hidden or not. */
    var total = countVisibleSteps();
    var stepNumber = visibleStepNumber(currentStep);
    nextButton.textContent = stepNumber === total - 1 ? 'Review Build' : 'Next';
    nextButton.style.display = 'inline-block';
  }

  function hideStepError(step) { var e = step.querySelector('.error-text'); if (e) e.style.display = 'none'; }
  function showStepError(step) { var e = step.querySelector('.error-text'); if (e) e.style.display = 'block'; }

  function validateCurrentStep() {
    var step = steps[currentStep];
    var stepName = step.dataset.step;
    hideStepError(step);
    var requiredByStep = {
      path: 'buildPath', miss: 'missType',
      'fit-stroke': 'strokeType', 'fit-speed': 'greenSpeed',
      'fit-feel': 'feelPreference', 'fit-miss-detail': 'missDetail',
      head: 'head', system: 'system', color: 'color',
      'shaft-length': 'shaftLength', 'shaft-color': 'shaftColor',
      'grip-thickness': 'gripThickness', 'grip-color': 'gripColor',
      hand: 'handOrientation'
    };
    // v2.1: artifact theme picker moved to the system step.
    if (stepName === 'system' && state.system === 'Artifact System' && !state.artifact) {
      showStepError(step); return false;
    }
    var requiredField = requiredByStep[stepName];
    if (requiredField && !state[requiredField]) { showStepError(step); return false; }
    return true;
  }

  function updateArtifactSavingsBadge() {
    var badge = document.getElementById('artifactSavingsBadge');
    if (!badge) return;
    if (state.head === 'Wing') badge.textContent = '−$125';
    else if (state.head === 'Blade') badge.textContent = '−$95';
    else badge.textContent = '−$95 / −$125';
  }

  function updateSystemDetails() {
    /* v2.1: EQ / CenterStrike / Artifact are all add-on options. */
    var panels = {
      'EQuilibrium System':  document.getElementById('equilibriumDetails'),
      'CenterStrike System': document.getElementById('centerStrikeDetails'),
      'Artifact System':     document.getElementById('artifactDetails')
    };
    Object.keys(panels).forEach(function (k) { if (panels[k]) panels[k].classList.remove('active'); });
    if (state.system && panels[state.system]) panels[state.system].classList.add('active');
    var themeOut = document.getElementById('artifactSelectedTheme');
    if (themeOut) themeOut.textContent = state.artifact || '-';

    var artImgGrid = document.getElementById('artifactSelectedImageGrid');
    var artImg = document.getElementById('artifactSelectedImage');
    var artCap = document.getElementById('artifactSelectedImageCaption');
    if (artImgGrid && artImg && artCap) {
      var theme = state.artifact
        ? (SKUS.artifactThemes || []).find(function (t) { return t.label === state.artifact; })
        : null;
      if (theme && theme.imageUrl) {
        artImg.src = theme.imageUrl;
        artImg.alt = state.artifact + ' artifact theme';
        artCap.textContent = state.artifact;
        artImgGrid.hidden = false;
      } else {
        artImg.removeAttribute('src');
        artImg.alt = '';
        artCap.textContent = '-';
        artImgGrid.hidden = true;
      }
    }
  }

  /* Line item properties — all visible (no underscore prefix) so customer sees the
     full selection on cart/checkout/email and admin sees the same on the order.

     Artifact builds keep Wing/Blade as the head; the artifact theme variant rides as a
     second-line component, and the Discount Function brings the build to $400. */
  function getShopifyCartProperties() {
    var raw = {
      'Build Source': state.buildPath === 'Guided' ? 'Recommended Build' : 'Final Configuration',
      'Build Path': state.buildPath || '-',
      'Biggest Miss': state.missType || 'N/A',
      'Stroke Type': state.strokeType || 'N/A',
      'Green Speed': state.greenSpeed || 'N/A',
      'Feel Preference': state.feelPreference || 'N/A',
      'Strike Location': state.missDetail || 'N/A',
      'Recommended Tuning': state.recommendedTuning || 'N/A',
      'Recommended Grip': state.recommendedGrip || 'N/A',
      'Head': state.head || '-',
      'System': state.system || '-',
      'Included Components': getIncludedComponents(),
      'Included Artifact': state.artifact || 'N/A',
      'Head Color': state.color || '-',
      'Shaft Length': state.shaftLength || '-',
      'Shaft Color': state.shaftColor || '-',
      'Grip Thickness': state.gripThickness || '-',
      'Grip Color': state.gripColor || '-',
      'Hand Orientation': state.handOrientation || '-',
      'Final Price': money(getTotalPrice())
    };
    // Drop properties whose value is 'N/A' so they don't render in cart drawer /
    // checkout / order confirmation. Manual-path orders skip most fit refinement Qs.
    var props = {};
    for (var k in raw) {
      if (Object.prototype.hasOwnProperty.call(raw, k) && raw[k] !== 'N/A') props[k] = raw[k];
    }
    return props;
  }

  /* Internal fit metadata sent to the customer-config worker (separate channel from cart).
     Persists fit answers + recommendation profile against customer.id for future visits. */
  function getFitMetadata() {
    return {
      buildSource: state.buildPath === 'Guided' ? 'Recommended Build' : 'Final Configuration',
      buildPath: state.buildPath || null,
      missType: state.missType || null,
      strokeType: state.strokeType || null,
      greenSpeed: state.greenSpeed || null,
      feelPreference: state.feelPreference || null,
      missDetail: state.missDetail || null,
      recommendedTuning: state.recommendedTuning || null,
      recommendedGrip: state.recommendedGrip || null,
      head: state.head || null,
      system: state.system || null,
      includedComponents: getIncludedComponents(),
      includedArtifact: state.artifact || null,
      headColor: state.color || null,
      shaftLength: state.shaftLength || null,
      shaftColor: state.shaftColor || null,
      gripThickness: state.gripThickness || null,
      gripColor: state.gripColor || null,
      handOrientation: state.handOrientation || null,
      finalPriceUsd: getTotalPrice(),
      capturedAt: new Date().toISOString()
    };
  }

  /* Resolve customer ID. Prefers theme.liquid's window.SHOPIFY_CUSTOMER_ID (set in
     New Customer Accounts flow after SSO handshake); falls back to the legacy
     section-injected window.__kgFitQuizCustomerId. */
  function getCustomerId() {
    if (typeof window.SHOPIFY_CUSTOMER_ID !== 'undefined' && window.SHOPIFY_CUSTOMER_ID) {
      return window.SHOPIFY_CUSTOMER_ID;
    }
    if (typeof window.__kgFitQuizCustomerId !== 'undefined' && window.__kgFitQuizCustomerId) {
      return window.__kgFitQuizCustomerId;
    }
    return null;
  }

  /* Fire-and-forget save to the configurator worker. Skipped silently for guests
     (no customer.id). Failures don't block the cart-add flow. 8s abort guard so
     a hung worker doesn't leak the request indefinitely. */
  var WORKER_TIMEOUT_MS = 8000;
  function saveFitMetadataToWorker() {
    var customerId = getCustomerId();
    debugGroup('saveFitMetadataToWorker');
    debug('customerId present:', !!customerId, '(type:', typeof customerId + ')');
    if (!customerId) {
      debug('No customer.id — guest checkout. Worker save skipped.');
      debugGroupEnd();
      return Promise.resolve(null);
    }
    var endpoint = SKUS.endpoints && SKUS.endpoints.saveFitMetadata;
    if (!endpoint) {
      debug('No saveFitMetadata endpoint configured in SKUs.');
      debugGroupEnd();
      return Promise.resolve(null);
    }
    var payload = { customerId: String(customerId), kind: 'fit-quiz', config: getFitMetadata() };
    debug('endpoint:', endpoint);

    var controller = null;
    var timeoutId = null;
    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };
    if (typeof AbortController === 'function') {
      controller = new AbortController();
      init.signal = controller.signal;
      timeoutId = setTimeout(function () {
        try { controller.abort(); } catch (e) {}
      }, WORKER_TIMEOUT_MS);
    }

    return fetch(endpoint, init).then(function (r) {
      if (timeoutId) clearTimeout(timeoutId);
      debug('response status:', r.status, r.ok ? 'OK' : 'FAIL');
      return r.text().then(function (text) {
        debug('response body length:', text.length);
        debugGroupEnd();
        return r;
      });
    }).catch(function (err) {
      if (timeoutId) clearTimeout(timeoutId);
      var aborted = err && (err.name === 'AbortError' || /aborted/i.test(err.message || ''));
      console.warn('[kg-fit-quiz] worker save failed (non-blocking):', aborted ? 'timeout' : err.name || 'error');
      debugGroupEnd();
    });
  }

  /* ========== SHOPIFY CART INTEGRATION (v2) ==========
   * Variant IDs come directly from the SKU map (Admin API verified). No /products/{handle}.js
   * fetch — that endpoint 404s for draft products and the new catalog has all options
   * decomposed into separate products anyway.
   *
   * Cart payload: ONE line item only — the head (Wing / Blade / Artifact theme variant).
   * All other selections (system, kit, shaft/grip options) ride as line item properties
   * on that head, visible in cart/checkout/email and on the order in admin. The kit and
   * option products are NOT added as separate cart lines — the fulfillment team reads
   * the properties to physically pack the kit.
   */

  /* Reverse-lookup: given a variant_id, return {handle, label} so we can print
     human-readable diagnostics when /cart/add.js fails. Covers every variant we send
     in the v4 multi-line cart payload: system-configuration, kit (3 systems × variants),
     and 6 option products. */
  function describeVariant(variantId) {
    var heads = SKUS.heads || {};
    for (var hk in heads) {
      if (heads[hk].variantId === variantId) return { handle: heads[hk].handle, label: 'Head: ' + hk };
    }
    var sysCfg = SKUS.systemConfiguration || {};
    if (sysCfg.variants) {
      for (var sk in sysCfg.variants) {
        if (sysCfg.variants[sk] === variantId) return { handle: sysCfg.handle, label: 'System: ' + sk };
      }
    }
    var kits = SKUS.kits || {};
    for (var k in kits) {
      var kvs = kits[k].variants || {};
      for (var kvk in kvs) {
        if (kvs[kvk] === variantId) return { handle: kits[k].handle, label: 'Kit (' + k + '): ' + kvk };
      }
    }
    var opts = SKUS.options || {};
    for (var ok in opts) {
      for (var v in opts[ok].variants) {
        if (opts[ok].variants[v] === variantId) return { handle: opts[ok].handle, label: 'Option ' + ok + ': ' + v };
      }
    }
    return null;
  }

  /* When /cart/add.js returns "Cannot find variant", print structured diagnostics so the
     team can tell at a glance whether the product is unreachable entirely vs only blocked
     for this market. Always runs (not DEBUG-gated) — this is a customer-facing failure. */
  function diagnoseCartAddFailure(body) {
    var items = (body && body.items) || [];
    try { console.group('[kg-fit-quiz] CART ADD FAILED — per-item diagnostic'); } catch (e) {}
    console.warn('Most likely cause when description="Cannot find variant": one of the variant IDs is on a DRAFT product OR unpublished from the Online Store sales channel for the current Market.');
    console.warn('Items POSTed (count=' + items.length + '):');
    items.forEach(function (item) {
      var info = describeVariant(item.id);
      if (!info) {
        console.warn('  ✗ variant=' + item.id + ' → UNKNOWN (not in SKU map — this is a bug, please report)');
        return;
      }
      console.warn('    variant=' + item.id + '  ' + info.label + '  [handle=' + info.handle + ']');
      var url = '/products/' + info.handle + '.js';
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (r.status === 200) {
            return r.json().then(function (prod) {
              var v = (prod.variants || []).find(function (x) { return Number(x.id) === Number(item.id); });
              if (v) {
                console.warn('    ↳ ' + url + ' OK — variant FOUND on storefront (available=' + v.available + ', price=' + v.price + ') ⇒ failure is probably Market/sales-channel restriction');
              } else {
                console.error('    ↳ ' + url + ' OK but variant ' + item.id + ' NOT in product.variants ⇒ variant deleted/replaced. Update kg-fit-quiz-skus.js with current variant ID.');
              }
            });
          }
          console.error('    ↳ ' + url + ' → HTTP ' + r.status + (r.status === 404 ? ' ⇒ product not on this storefront (DRAFT or unpublished)' : ''));
        })
        .catch(function (e) { console.warn('    ↳ ' + url + ' fetch error: ' + e.message); });
    });
    console.warn('Quiz state at time of failure:', {
      head: state.head, artifact: state.artifact, system: state.system,
      color: state.color, shaftColor: state.shaftColor, shaftLength: state.shaftLength,
      gripColor: state.gripColor, gripThickness: state.gripThickness,
      handOrientation: state.handOrientation
    });
    try { console.groupEnd(); } catch (e) {}
  }

  /* Default to Right Hand if user hasn't reached the hand step (e.g. Recommended path
     short-circuits to add-to-cart). RH stock is non-zero; LH stock is currently 0 but
     allowed via inventory_policy=continue. */
  function getSelectedHand() {
    return state.handOrientation === 'Left Hand' ? 'Left Hand' : 'Right Hand';
  }

  /* v4.2: head shape (Wing / Blade) IS the cart parent — carries the build price.
     Not fit-quiz-component-tagged, but the cart-drawer Liquid filter explicitly allows
     `the-wing` and `the-blade` to render. Cart Transform Function reads `_kgLineRole`
     ('head') to identify the merge parent at checkout. */
  function resolveHeadLineItem() {
    var heads = SKUS.heads || {};
    var h = heads[state.head];
    if (!h || !h.variantId) return null;
    return { id: h.variantId, role: 'Head', label: state.head };
  }

  function resolveSystemConfigurationLineItem() {
    /* The marker line that names the system. Required for every build. */
    var sysCfg = SKUS.systemConfiguration;
    if (!sysCfg || !sysCfg.variants) return null;
    var vid = sysCfg.variants[state.system];
    return vid ? { id: vid, role: 'System', label: state.system } : null;
  }

  function resolveKitLineItems() {
    /* EQuilibrium / CenterStrike: ship the FULL kit — every variant inside
       (EQ65 + Base 40 + Base 60 for EQ, Anchors 25/50/75/150 for CS) goes on its
       own cart line so admin/fulfillment sees one row per physical kit piece.
       Artifact System: only the customer-picked theme variant (state.artifact). */
    var kit = SKUS.kits[state.system];
    if (!kit || !kit.variants) return [];

    if (state.system === 'Artifact System') {
      var vid = state.artifact && kit.variants[state.artifact];
      return vid ? [{ id: vid, role: kit.label || 'Kit', label: state.artifact }] : [];
    }

    var items = [];
    for (var variantName in kit.variants) {
      if (!Object.prototype.hasOwnProperty.call(kit.variants, variantName)) continue;
      items.push({ id: kit.variants[variantName], role: kit.label || 'Kit', label: variantName });
    }
    return items;
  }

  function resolveOptionLineItems() {
    /* Six tagged option products. Skip any field with no selection or no matching
       variant — better to ship a partial build than block the cart-add hard. */
    var picks = [
      { field: 'handOrientation', value: state.handOrientation },
      { field: 'putterColor',     value: state.color },
      { field: 'shaftLength',     value: state.shaftLength },
      { field: 'shaftColor',      value: state.shaftColor },
      { field: 'gripColor',       value: state.gripColor },
      { field: 'gripThickness',   value: state.gripThickness }
    ];
    var items = [];
    picks.forEach(function (p) {
      var opt = SKUS.options[p.field];
      if (!opt || !opt.variants) return;
      var vid = opt.variants[p.value];
      if (vid) items.push({ id: vid, role: opt.role || p.field, label: p.value });
    });
    return items;
  }

  function companionProperties(role, label, buildId, kgLineRole, kgPath, extras) {
    /* Underscore-prefixed properties are hidden from cart/checkout/email by Shopify but
       still visible to admin/fulfillment — exactly what we want for the grouping markers.
       'Component Role' stays visible so customers can see e.g. "Kit" or "Shaft Color"
       next to each $0 line.

       _kgPath / _kgLineRole are the structured signals the Cart Transform Function reads
       to decide which line is the merge parent. The function does no inference from
       handle or product tag — it trusts these labels verbatim. Path is one of
       'standard' | 'artifact'. Role is one of 'head' | 'theme' | 'kit' | 'option'. */
    var p = {
      '_kgBuildId': buildId,
      '_kgPath': kgPath,
      '_kgLineRole': kgLineRole,
      'Component Role': role,
      'Selection': label || '-'
    };
    if (extras) {
      for (var k in extras) {
        if (Object.prototype.hasOwnProperty.call(extras, k)) p[k] = extras[k];
      }
    }
    return p;
  }

  function getSelectedArtifactImageUrl() {
    /* Picked artifact's CDN photo URL, used by the Cart Transform Function via
       `_kgArtifactImage` to override the merged-line image at checkout. */
    if (state.system !== 'Artifact System' || !state.artifact) return null;
    var themes = SKUS.artifactThemes || [];
    for (var i = 0; i < themes.length; i++) {
      if (themes[i].label === state.artifact) {
        return themes[i].imageUrl && /^https?:\/\//.test(themes[i].imageUrl) ? themes[i].imageUrl : null;
      }
    }
    return null;
  }

  function buildLineItems() {
    /* v4.2 multi-line cart, 9 lines per build. The Wing/Blade head is the priced
       parent ($525/$495). Everything else is a tagged $0 child marker, regrouped at
       checkout by the Cart Transform Function via `_kgBuildId` and `_kgLineRole='head'`.

       Lines emitted (in POST order — cart drawer renders most-recent on top, so the
       head goes LAST to land on top):
         children: 6 options + 1 system kit + 1 system-configuration
         parent:   head (Wing or Blade) */
    var items = [];
    var buildId = 'kg-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    var kgPath = (state.system === 'Artifact System') ? 'artifact' : 'standard';
    var artifactImageUrl = getSelectedArtifactImageUrl();
    var artifactExtras = artifactImageUrl ? { '_kgArtifactImage': artifactImageUrl } : null;

    /* options (6 child lines) */
    resolveOptionLineItems().forEach(function (it) {
      items.push({ id: it.id, quantity: 1, properties: companionProperties(it.role, it.label, buildId, 'option', kgPath) });
    });

    /* kit child line(s) — 1 for Artifact, 3 for EQ, 4 for CenterStrike */
    var kitLines = resolveKitLineItems();
    if (kitLines.length === 0) {
      var kerr = new Error('No system kit selected. system=' + state.system + ' artifact=' + state.artifact);
      kerr.code = 'KG_NO_KIT';
      throw kerr;
    }
    var kitExtras = (state.system === 'Artifact System') ? artifactExtras : null;
    kitLines.forEach(function (kl) {
      items.push({ id: kl.id, quantity: 1, properties: companionProperties(kl.role, kl.label, buildId, 'kit', kgPath, kitExtras) });
    });

    /* system-configuration child line — required */
    var sysLine = resolveSystemConfigurationLineItem();
    if (!sysLine) {
      var serr = new Error('No system selected.');
      serr.code = 'KG_NO_SYSTEM';
      throw serr;
    }
    items.push({ id: sysLine.id, quantity: 1, properties: companionProperties(sysLine.role, sysLine.label, buildId, 'system', kgPath) });

    /* head parent line — Wing/Blade, carries the build price. Posted LAST so it
       appears at the top of the cart drawer. */
    var head = resolveHeadLineItem();
    if (!head) {
      var herr = new Error('No head selected. state.head=' + state.head);
      herr.code = 'KG_NO_HEAD';
      throw herr;
    }
    var headProps = getShopifyCartProperties();
    headProps['_kgBuildId'] = buildId;
    headProps['_kgPath'] = kgPath;
    headProps['_kgLineRole'] = 'head';
    if (artifactImageUrl) headProps['_kgArtifactImage'] = artifactImageUrl;
    items.push({ id: head.id, quantity: 1, properties: headProps });

    return items;
  }

  /* The theme's xo-cart-mini subscriber reads state.sections[this.id] where this.id is
   * the literal element id "xo-cart-mini". Shopify's Sections API resolves the same
   * name "xo-cart-mini" against snippets/xo-cart-mini.liquid (a section file in this theme).
   *
   * Using the parent .shopify-section id (e.g. "sections--xxx__cart") makes Shopify return
   * HTML under a different key, so the cart-mini subscriber never finds it and renders empty.
   *
   * Match the theme's own getServiceSections() pattern — pass the element id directly. */
  function getCartMiniSectionId() {
    return document.querySelector('xo-cart-mini') ? 'xo-cart-mini' : null;
  }

  function postCartAdd(items) {
    var sectionId = getCartMiniSectionId();
    var body = { items: items };
    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }
    /* Always log the payload so we can diff what we send vs. what 422'd. Each item
       gets a describeVariant() lookup so the row is readable without inspecting IDs. */
    console.log('[kg-fit-quiz] POST /cart/add.js, items=' + items.length);
    items.forEach(function (item, idx) {
      var info = describeVariant(item.id);
      console.log('  [' + (idx+1) + '] variant=' + item.id + (info ? '  ' + info.label + ' (handle=' + info.handle + ')' : '  UNKNOWN'));
    });
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (resBody) {
        if (!r.ok) {
          console.error('[kg-fit-quiz] /cart/add.js ' + r.status, resBody && resBody.description);
          diagnoseCartAddFailure(body);
          var msg = resBody && (resBody.description || resBody.message) || ('HTTP ' + r.status);
          throw new Error(msg);
        }
        return resBody;
      });
    });
  }

  /* Theme uses window.xoStore as the cart state store. The xo-cart-mini web component
   * subscribes to it and re-renders when state.sections changes. If we only swap section
   * innerHTML the component's internal state goes stale — the 1st add works by accident,
   * subsequent adds render an empty drawer because the store still says "empty".
   *
   * Mirror the theme's own addCartSuccess: write data.sections + items/size + isAdded
   * into the "xo-cart" key so cart-mini re-renders against the new state.
   *
   * Falls back to manual section innerHTML swap if xoStore isn't on window for some reason. */
  function refreshCartMiniSection(result, sectionId) {
    if (!result) return;

    if (window.xoStore && typeof window.xoStore.set === 'function') {
      try {
        var items = (result && result.items) || [];
        var size = items.reduce(function (acc, item) { return acc + (item.quantity || 0); }, 0);
        var productIds = items.map(function (it) { return String(it.product_id || it.id); });
        window.xoStore.set('xo-cart', function (prev) {
          return Object.assign({}, prev, {
            size: size,
            sections: result.sections || (prev && prev.sections) || {},
            items: items,
            addIdLoading: '',
            isAdded: true,
            productIdsForCartNotification: productIds
          });
        })('xo-cart/addCartSuccess');
        return;
      } catch (e) {
        console.warn('[kg-fit-quiz] xoStore update failed, falling back to manual section swap:', e && e.message);
      }
    }

    /* Fallback: direct innerHTML swap (theme without xoStore or update failed). */
    if (!result.sections || !sectionId) return;
    var html = result.sections[sectionId];
    if (!html) return;
    var sectionEl = document.getElementById('shopify-section-' + sectionId);
    if (!sectionEl) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var inner = tmp.querySelector('#shopify-section-' + sectionId) || tmp;
    sectionEl.innerHTML = inner.innerHTML;
  }

  /* Open the XO cart drawer (slides from the right). Prefer the public xoModal API;
   * fall back to clicking the header cart trigger; if both are missing, redirect to
   * /cart so the user still sees what got added. */
  function openCartDrawer() {
    try {
      if (window.xoModal && typeof window.xoModal.open === 'function') {
        window.xoModal.open('cart');
        return true;
      }
      var trigger = document.querySelector('xo-modal-trigger[xo-name="cart"]');
      if (trigger) { trigger.click(); return true; }
    } catch (e) {
      console.warn('[kg-fit-quiz] openCartDrawer failed:', e && e.message);
    }
    return false;
  }


  /* Lock the triggering button while cart-add is in flight. Multiple network calls
   * (variant lookups + /cart/add.js with sections) can take 2–5s, and an unlocked
   * button gets double-clicked → duplicate cart lines. */
  function setButtonLoading(btn, isLoading) {
    if (!btn) return;
    if (isLoading) {
      if (btn.dataset.kgOriginalHtml == null) btn.dataset.kgOriginalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('kg-loading');
      btn.innerHTML = '<span class="kg-cart-spinner" aria-hidden="true"></span>Adding…';
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('kg-loading');
      if (btn.dataset.kgOriginalHtml != null) {
        btn.innerHTML = btn.dataset.kgOriginalHtml;
        delete btn.dataset.kgOriginalHtml;
      }
    }
  }

  function addConfigurationToCart(source, triggerButton) {
    /* In-flight guard. Stored on window so it's shared across script-load scopes —
       if defer + duplicate script tag causes two IIFEs to run, both see the same lock.
       Catches duplicate listeners, fast double-click, and any other path that would
       otherwise POST /cart/add.js twice and end up with 2x the same item. */
    if (window.__kgFitQuizCartAddInFlight) {
      debug('cart-add already in flight — ignoring duplicate call from', source);
      return Promise.resolve(null);
    }
    window.__kgFitQuizCartAddInFlight = true;
    debug('Building cart payload for', source);
    var sectionId = getCartMiniSectionId();
    setButtonLoading(triggerButton, true);

    function release() {
      setButtonLoading(triggerButton, false);
      window.__kgFitQuizCartAddInFlight = false;
    }

    var items;
    try {
      items = buildLineItems();
    } catch (err) {
      release();
      if (err && err.code === 'KG_NO_HEAD') {
        showToast('Please select a putter head first.', 'error');
        var headIdx = getStepIndexByName('head');
        if (headIdx !== -1) scrollToStep(headIdx);
        return Promise.resolve(null);
      }
      if (err && err.code === 'KG_NO_SYSTEM') {
        showToast('Please select a system first.', 'error');
        var sysIdx = getStepIndexByName('system');
        if (sysIdx !== -1) scrollToStep(sysIdx);
        return Promise.resolve(null);
      }
      if (err && err.code === 'KG_NO_KIT') {
        showToast(state.system === 'Artifact System'
          ? 'Please pick an artifact first.'
          : 'No kit configured for this system. Contact support.', 'error');
        var sysrIdx = getStepIndexByName('system-review');
        if (sysrIdx !== -1) scrollToStep(sysrIdx);
        return Promise.resolve(null);
      }
      console.error('[kg-fit-quiz] buildLineItems failed:', err && err.message);
      showToast('Something went wrong adding to cart. Please try again in a moment.', 'error');
      return Promise.reject(err);
    }
    if (!items || items.length === 0) {
      release();
      showToast('No items to add to cart. Please review your selections.', 'error');
      return Promise.resolve(null);
    }
    debug('line items:', items.length);

    return postCartAdd(items).then(function (result) {
      debug('cart add success');
      // Fire-and-forget customer metadata save. Don't block UI on it.
      saveFitMetadataToWorker();
      refreshCartMiniSection(result, sectionId);
      if (typeof closeFitQuiz === 'function') closeFitQuiz();
      var opened = openCartDrawer();
      if (!opened) window.location.href = '/cart';
      release();
      return result;
    }).catch(function (err) {
      release();
      console.error('[kg-fit-quiz] cart add failed:', err && err.message);
      /* v2 lenient: storefront-side availability errors (Cannot find variant / sold out /
         unavailable) are expected during the testing window when new products aren't
         published to every market. Don't block the user — surface a soft toast, close
         the quiz, and let them keep moving. Real failures get a louder error toast. */
      var benign = /cannot find variant|variant.*unavailable|unavailable|sold out|out of stock/i.test(err.message || '');
      if (benign) {
        debug('benign cart-add error suppressed (lenient mode).');
        saveFitMetadataToWorker();
        showToast('Some options are not yet available for purchase and could not be added. Our team has been automatically notified.', 'info');
        if (typeof closeFitQuiz === 'function') closeFitQuiz();
        return;
      }
      showToast('Failed to add to cart: ' + (err.message || 'Please try again in a moment.'), 'error');
      throw err;
    });
  }

  function hasRecommendedCartDetails() {
    return Boolean(state.color && state.shaftLength && state.shaftColor && state.gripColor && state.handOrientation);
  }

  function syncRecommendedDetailCards() {
    var groups = document.querySelectorAll('[data-rec-field]');
    for (var i = 0; i < groups.length; i++) {
      var field = groups[i].dataset.recField;
      var cards = groups[i].querySelectorAll('.color-choice-card');
      for (var j = 0; j < cards.length; j++) cards[j].classList.toggle('selected', cards[j].dataset.value === state[field]);
    }
  }

  function showRecommendedDetailPanel() {
    recommendedColorPanel.classList.add('active');
    recommendedColorError.style.display = 'none';
    syncRecommendedDetailCards();
  }

  function addRecommendedBuildToCartAfterDetailCheck(triggerButton) {
    applyRefinedDefaults();
    if (!hasRecommendedCartDetails()) {
      showRecommendedDetailPanel();
      return null;
    }
    if (recommendedCartStatus) recommendedCartStatus.style.display = 'block';
    return addConfigurationToCart('Recommended Build', triggerButton);
  }

  function renderSummary() {
    summaryList.innerHTML = '';
    var rec = getDetailedRecommendation();
    var isArtifact = state.system === 'Artifact System';
    var headLabel = (state.head || '-') + ' (' + money(getBasePrice()) + ')';
    var addOnLabel = (state.system || '-') + (isArtifact
      ? ' — total $' + ARTIFACT_TARGET_PRICE + ' after upgrade discount'
      : ' (included)');
    var rows = [
      ['Build Path', state.buildPath || '-'],
      ['Biggest Miss', state.missType || 'N/A'],
      ['Recommendation', state.buildPath === 'Guided' && rec ? rec.reason : 'Custom manual build'],
      ['Stroke Type', state.strokeType || 'N/A'],
      ['Green Speed', state.greenSpeed || 'N/A'],
      ['Feel Preference', state.feelPreference || 'N/A'],
      ['Strike Location', state.missDetail || 'N/A'],
      ['Recommended Tuning', state.recommendedTuning || 'N/A'],
      ['Recommended Grip', state.recommendedGrip || 'N/A'],
      ['Head', headLabel],
      ['Add-on', addOnLabel],
      ['Included Components', getIncludedComponents()],
      ['Head Color', state.color || '-'],
      ['Shaft Length', state.shaftLength || '-'],
      ['Shaft Color', state.shaftColor || '-'],
      ['Grip Thickness', state.gripThickness || '-'],
      ['Grip Color', state.gripColor || '-'],
      ['Hand Orientation', state.handOrientation || '-']
    ];
    // Hide rows the user never filled in (Manual path skips most fit refinement Qs).
    rows = rows.filter(function (r) { return r[1] !== 'N/A'; });
    rows.forEach(function (r) {
      var row = document.createElement('div');
      row.className = 'summary-row';
      row.innerHTML = '<span>' + r[0] + '</span><span>' + r[1] + '</span>';
      summaryList.appendChild(row);
    });
    finalPrice.textContent = money(getTotalPrice());
  }

  function renderArtifactGrids() {
    /* v2.1: system-step artifact theme picker (moved from head step).
       Each card uses the image-swatch pattern from the landing-page draft —
       <img> with a placeholder URL on top of a swatch fallback that prints the theme name.
       Real images take over the moment a live URL replaces the placeholder string. */
    var grids = document.querySelectorAll('[data-field="artifact"], #systemArtifactGrid');
    for (var g = 0; g < grids.length; g++) {
      grids[g].innerHTML = artifactItems.map(function (item) {
        var label = escapeAttr(item.label);
        var media = isLiveImageUrl(item.imageUrl)
          ? '<img src="' + escapeAttr(item.imageUrl) + '" alt="' + label + ' artifact theme" loading="lazy">'
          : '<div class="swatch-placeholder" data-placeholder-url="' + escapeAttr(item.imageUrl || '') + '">' + label + '</div>';
        return '<button class="artifact-card" data-value="' + label + '" type="button">'
          + '<div class="image-swatch">' + media + '</div>'
          + '<strong>' + label + '</strong>'
          + '</button>';
      }).join('');
    }
  }

  function updateArtifactGridVisibility() {
    var grid = document.getElementById('systemArtifactGrid');
    if (!grid) return;
    grid.classList.toggle('hidden', state.system !== 'Artifact System');
  }

  function bindEvents() {
    document.addEventListener('click', function (event) {
      var optionCard = event.target.closest && event.target.closest('.option-card');
      if (optionCard && !optionCard.classList.contains('disabled')) {
        var grid = optionCard.closest('.option-grid');
        var field = grid.dataset.field;
        state[field] = optionCard.dataset.value;
        var siblings = grid.querySelectorAll('.option-card');
        for (var i = 0; i < siblings.length; i++) siblings[i].classList.remove('selected');
        optionCard.classList.add('selected');
        hideStepError(optionCard.closest('.step'));
        if (field === 'buildPath') {
          if (state.buildPath === 'Manual') {
            state.missType = null;
            updateGuidedBanners();
          }
          applyBuildPathStepVisibility();
          updateProgress(); // refresh "Step N of M" since total visible just changed
        }
        if (field === 'missType') applyGuidedRecommendation();
        if (['strokeType', 'greenSpeed', 'feelPreference', 'missDetail'].indexOf(field) !== -1) applyRefinedDefaults();
        if (field === 'head') {
          updateGuidedBanners();
          updateArtifactSavingsBadge();
        }
        if (field === 'color') autoSelectGripColor();
        if (field === 'shaftColor' || field === 'handOrientation') enforceShaftHandConstraint();
        if (field === 'system') {
          // v2.1: artifact theme picker lives in the system step; clear theme when leaving Artifact.
          if (state.system !== 'Artifact System') {
            state.artifact = null;
            var arts = document.querySelectorAll('.artifact-card');
            for (var ka = 0; ka < arts.length; ka++) arts[ka].classList.remove('selected');
          }
          updateArtifactGridVisibility();
          updateGuidedBanners();
          updateSystemDetails();
        }
      }

      var artifactCard = event.target.closest && event.target.closest('.artifact-card');
      if (artifactCard) {
        state.artifact = artifactCard.dataset.value;
        var aGrid = artifactCard.closest('.artifact-grid');
        var aSibs = aGrid.querySelectorAll('.artifact-card');
        for (var m = 0; m < aSibs.length; m++) aSibs[m].classList.remove('selected');
        artifactCard.classList.add('selected');
        hideStepError(artifactCard.closest('.step'));
        updateSystemDetails();
      }

      var rec = event.target.closest && event.target.closest('.color-choice-card');
      if (rec && !rec.classList.contains('disabled')) {
        var group = rec.closest('[data-rec-field]');
        var field = group.dataset.recField;
        state[field] = rec.dataset.value;
        var rsibs = group.querySelectorAll('.color-choice-card');
        for (var n = 0; n < rsibs.length; n++) rsibs[n].classList.remove('selected');
        rec.classList.add('selected');
        setSelectedCard(field, state[field]);
        if (field === 'color') autoSelectGripColor();
        if (field === 'shaftColor' || field === 'handOrientation') enforceShaftHandConstraint();
        if (recommendedColorError) recommendedColorError.style.display = 'none';
        return;
      }

    });

    nextButton.addEventListener('click', function () {
      if (!validateCurrentStep()) return;
      var stepName = steps[currentStep].dataset.step;
      if (stepName === 'path' && state.buildPath === 'Manual') { scrollToStep(getStepIndexByName('head')); return; }
      if (stepName === 'path' && state.buildPath === 'Guided') { scrollToStep(getStepIndexByName('miss')); return; }
      if (stepName === 'miss' && state.buildPath === 'Guided') { applyGuidedRecommendation(); scrollToStep(getStepIndexByName('fit-stroke')); return; }
      if (['fit-stroke', 'fit-speed', 'fit-feel', 'fit-miss-detail'].indexOf(stepName) !== -1 && state.buildPath === 'Guided') {
        applyRefinedDefaults();
        if (stepName === 'fit-miss-detail') { scrollToStep(getStepIndexByName('recommendation')); return; }
      }
      scrollToStep(currentStep + 1);
    });

    backButton.addEventListener('click', function () {
      var stepName = steps[currentStep].dataset.step;
      if (stepName === 'head' && state.buildPath === 'Manual') { scrollToStep(getStepIndexByName('path')); return; }
      if (stepName === 'head' && state.buildPath === 'Guided') { scrollToStep(getStepIndexByName('recommendation')); return; }
      if (stepName === 'recommendation' && state.buildPath === 'Guided') { scrollToStep(getStepIndexByName('fit-miss-detail')); return; }
      if (stepName === 'fit-stroke' && state.buildPath === 'Guided') { scrollToStep(getStepIndexByName('miss')); return; }
      scrollToStep(currentStep - 1);
    });

    customizeRecommendedButton.addEventListener('click', function () { applyRefinedDefaults(); scrollToStep(getStepIndexByName('head')); });
    changeMissButton.addEventListener('click', function () { scrollToStep(getStepIndexByName('miss')); });
    closeRecommendedButton.addEventListener('click', function () { closeFitQuiz(); });
    addRecommendedToCartButton.addEventListener('click', function (e) { addRecommendedBuildToCartAfterDetailCheck(e.currentTarget); });
    confirmRecommendedCartButton.addEventListener('click', function (e) {
      if (!hasRecommendedCartDetails()) { recommendedColorError.style.display = 'block'; return; }
      addRecommendedBuildToCartAfterDetailCheck(e.currentTarget);
    });
    addToCartButton.addEventListener('click', function (e) { addConfigurationToCart('Final Configuration', e.currentTarget); });
  }

  function autoSelectGripColor() {
    /* v2: grip color is its own product with Black/Silver variants. Auto-match head color
       so checkout has a sensible default; user can override on the grip-color step. */
    if (!state.color) return;
    state.gripColor = state.color; // 'Black' or 'Silver' — matches product variant strings
    setSelectedCard('gripColor', state.gripColor);
  }

  function enforceShaftHandConstraint() {
    /* The Silver (Chrome) shaft is not produced in Left Hand orientation. When the
       current pair violates this — whichever side the user just changed — flip the
       shaft to Black and surface a formal notice so they understand the swap.
       UI uses 'Chrome' as the Silver shaft label. */
    var isSilverShaft = state.shaftColor === 'Chrome' || state.shaftColor === 'Silver';
    if (isSilverShaft && state.handOrientation === 'Left Hand') {
      state.shaftColor = 'Black';
      setSelectedCard('shaftColor', 'Black');
      showToast('Heads up — the Silver shaft isn’t available for Left Hand builds yet. We’ve switched your shaft color to Black so checkout stays valid.', 'warning');
    }
  }

  function applyDisabledOptions() {
    // Hand orientation: both RH and LH are selectable now that LH variants exist.
    // RH stays as the default-selected card.
    var lhCards = document.querySelectorAll('[data-field="handOrientation"] .option-card');
    for (var i = 0; i < lhCards.length; i++) {
      if (lhCards[i].dataset.value === 'Right Hand') {
        lhCards[i].classList.add('selected');
      }
    }
    // Grip color is freely selectable. Auto-match to head color via autoSelectGripColor()
    // happens as a default; user can override on the grip-color step.
  }

  function init() {
    /* Guard against double-init. Section renders or theme JS quirks can occasionally
       evaluate this IIFE twice, doubling every listener (and every /cart/add.js POST). */
    if (window.__kgFitQuizInited) {
      console.warn('[kg-fit-quiz] init() called more than once — skipping duplicate bind.');
      return;
    }
    window.__kgFitQuizInited = true;
    quizShell = document.getElementById('quizShell');
    stepsContainer = document.getElementById('stepsContainer');
    steps = Array.prototype.slice.call(document.querySelectorAll('.step'));
    backButton = document.getElementById('backButton');
    nextButton = document.getElementById('nextButton');
    progressLabel = document.getElementById('progressLabel');
    progressPercent = document.getElementById('progressPercent');
    progressFill = document.getElementById('progressFill');
    summaryList = document.getElementById('summaryList');
    finalPrice = document.getElementById('finalPrice');
    addToCartButton = document.getElementById('addToCartButton');
    cartStatus = document.getElementById('cartStatus');
    customizeRecommendedButton = document.getElementById('customizeRecommendedButton');
    changeMissButton = document.getElementById('changeMissButton');
    addRecommendedToCartButton = document.getElementById('addRecommendedToCartButton');
    closeRecommendedButton = document.getElementById('closeRecommendedButton');
    recommendedCartStatus = document.getElementById('recommendedCartStatus');
    recommendedPrice = document.getElementById('recommendedPrice');
    recommendedColorPanel = document.getElementById('recommendedColorPanel');
    confirmRecommendedCartButton = document.getElementById('confirmRecommendedCartButton');
    recommendedColorError = document.getElementById('recommendedColorError');

    if (!quizShell) return; // section not on page

    /* Diagnostic: customer + endpoint state on init. PII (the actual customerId) is only
       printed when DEBUG is on. Default boot logs only the resolved/missing flag. */
    if (DEBUG) {
      var diag = {
        shopifyCustomerIdType: typeof window.SHOPIFY_CUSTOMER_ID,
        hasShopifyCustomerId: !!window.SHOPIFY_CUSTOMER_ID,
        hasLegacyKgFitQuizCustomerId: !!window.__kgFitQuizCustomerId,
        isLoggedIn: Boolean(getCustomerId()),
        saveEndpoint: SKUS.endpoints && SKUS.endpoints.saveFitMetadata
      };
      debug('init diagnostics:', diag);
      if (!diag.isLoggedIn) {
        debug('no customer ID resolved. SSO bridge (?sso=silent) should fire on first __kgFitQuizClick() if logged in.');
      }
    }

    initializeTheme();
    renderArtifactGrids();
    updateArtifactGridVisibility();
    updateSystemDetails();
    updateGuidedBanners();
    updateRecommendedBuildScreen();
    bindEvents();
    applyDisabledOptions();
    updateProgress();
    updateButtons();
    bindModalEvents();
  }

  /* ========== Modal open/close API (matches kg-configurator pattern) ========== */

  function getModal() { return document.getElementById('kgFitQuizModal'); }

  /* Reset every UI surface back to step 1 / unselected. Called on close so the
     next open is a clean slate — the original implementation kept stale state
     and the user saw their previous answers when they re-opened. */
  function resetQuizState() {
    state = defaultState();
    currentStep = 0;
    var selected = document.querySelectorAll('#kgFitQuizModal .option-card.selected, #kgFitQuizModal .artifact-card.selected, #kgFitQuizModal .color-choice-card.selected');
    for (var i = 0; i < selected.length; i++) selected[i].classList.remove('selected');
    var errs = document.querySelectorAll('#kgFitQuizModal .error-text');
    for (var j = 0; j < errs.length; j++) errs[j].style.display = 'none';
    if (recommendedColorPanel) recommendedColorPanel.classList.remove('active');
    if (recommendedColorError) recommendedColorError.style.display = 'none';
    if (recommendedCartStatus) recommendedCartStatus.style.display = 'none';
    if (cartStatus) cartStatus.style.display = 'none';
    if (stepsContainer) stepsContainer.scrollLeft = 0;
    if (steps && steps.length) steps[0].scrollIntoView({ behavior: 'auto', inline: 'start' });
    applyBuildPathStepVisibility(); // un-hide guided steps (state.buildPath is null after reset)
    applyDisabledOptions(); // re-mark RH selected by default
    updateArtifactGridVisibility();
    updateSystemDetails();
    updateGuidedBanners();
    updateRecommendedBuildScreen();
    updateProgress();
    updateButtons();
  }

  /* Lightweight in-modal toast. Replaces the old `alert()` so users get a friendly,
     dismissable message instead of a system dialog. Auto-dismisses after 5s; an
     `error`-variant toast stays visible until the user dismisses or calls again. */
  var toastTimer = null;
  function showToast(message, variant) {
    var modal = getModal();
    if (!modal) return;
    var el = document.getElementById('kgFitQuizToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'kgFitQuizToast';
      el.className = 'kg-fit-quiz-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      modal.appendChild(el);
    }
    el.textContent = message;
    el.classList.remove('variant-error', 'variant-info', 'variant-success', 'variant-warning');
    el.classList.add('variant-' + (variant || 'info'));
    el.classList.add('show');
    if (toastTimer) clearTimeout(toastTimer);
    if (variant !== 'error') {
      toastTimer = setTimeout(function () { el.classList.remove('show'); }, 5000);
    }
  }
  function hideToast() {
    var el = document.getElementById('kgFitQuizToast');
    if (el) el.classList.remove('show');
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
  }

  function openFitQuiz() {
    var modal = getModal();
    if (!modal) { console.warn('[kg-fit-quiz] modal markup not on page'); return; }
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.remove('light-mode'); // belt-and-suspenders against theme overrides
    document.body.classList.add('kg-fit-quiz-active');
    // Inline style with !important — last line of defense if theme CSS still wins.
    modal.style.setProperty('background-color', '#0b0b0d', 'important');
    modal.style.setProperty('background-image', 'none', 'important');
    modal.style.setProperty('display', 'block', 'important');
    var shell = modal.querySelector('.quiz-shell');
    if (shell) {
      shell.style.setProperty('background-color', '#0b0b0d', 'important');
      shell.style.setProperty('background-image',
        'radial-gradient(circle at top left, rgba(212,175,55,0.12), transparent 34%), linear-gradient(135deg, #09090b 0%, #151518 100%)',
        'important');
    }
    /* Force the steps carousel back to step 0 *after* the modal becomes visible.
       Setting scrollLeft while the container is display:none is a no-op (browsers
       restore the prior scroll position when the element is shown again), so the
       same line inside resetQuizState() can't fix close→reopen by itself.
       requestAnimationFrame ensures layout has flushed before we set scrollLeft.
       Run resetQuizState() inside the rAF too so every UI surface reset (scroll,
       .selected classes, inline styles, hidden panels) happens after the modal is
       visible — close→reopen now always lands on a clean step 1, regardless of
       any leftover state from a previous session. */
    requestAnimationFrame(function () {
      resetQuizState();
      forceScrollToStart();
      /* Second pass with the snap-disable trick — Safari occasionally restores
         the prior scroll position after the first paint following display:block,
         and mandatory scroll-snap will pin scrollLeft to a remembered snap point
         even after we set it to 0. Running forceScrollToStart() a second time
         on the next frame wins decisively. */
      requestAnimationFrame(forceScrollToStart);
    });
  }

  function closeFitQuiz() {
    var modal = getModal();
    if (!modal) return;
    /* Reset stepsContainer scroll BEFORE hiding the modal. Once display:none
       hits, setting scrollLeft is a no-op, AND Safari's mandatory scroll-snap
       persists the prior snap point across the display toggle (verified via
       diagnostic: scrollLeft pinned to 10×viewport after reopen). Using
       forceScrollToStart() disables snap, sets scroll to 0, re-enables snap
       on next frame — beats the snap-restore. */
    forceScrollToStart();
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('kg-fit-quiz-active');
    /* openFitQuiz() pins several inline !important styles on the modal and shell to
       beat theme CSS. Removing the .open class isn't enough — clear every inline
       override here so the next open starts from an unbiased state (and the modal
       doesn't linger visible after close). */
    modal.style.removeProperty('display');
    modal.style.removeProperty('background-color');
    modal.style.removeProperty('background-image');
    var shell = modal.querySelector('.quiz-shell');
    if (shell) {
      shell.style.removeProperty('background-color');
      shell.style.removeProperty('background-image');
    }
    hideToast();
    resetQuizState();
  }

  function bindModalEvents() {
    var closeBtn = document.getElementById('kgFitQuizClose');
    if (closeBtn) closeBtn.addEventListener('click', closeFitQuiz);
    var startOverBtn = document.getElementById('kgFitQuizStartOver');
    if (startOverBtn) {
      startOverBtn.addEventListener('click', function () {
        resetQuizState();
        showToast('Starting over from the beginning.', 'info');
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        var modal = getModal();
        if (modal && modal.classList.contains('open')) closeFitQuiz();
      }
    });
  }

  window.__kgOpenFitQuiz = openFitQuiz;
  window.__kgCloseFitQuiz = closeFitQuiz;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
